/**
 * System Map — an in-console graph of the NUREV Brain vault (the Obsidian
 * LLM-wiki that maps the whole system). Content is served admin-gated from
 * nurev-api /admin-brain/*; this screen renders an Obsidian-style force graph,
 * and clicking a node opens that page inline in an on-graph detail drawer with
 * `[[wikilink]]` navigation and GFM markdown.
 *
 * Internal-only content (compliance, security, money rails) — it reaches the
 * browser only after an authenticated admin request, never via the bundle.
 */
import { useMemo, useState, useEffect, lazy, Suspense } from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Network, Maximize2, Minimize2, X } from 'lucide-react';
import { useBrainTree, useBrainPage } from '@/hooks/useBrain';
import { Skeleton } from '@/components/ui/atoms';

const BrainGraph = lazy(() =>
  import('./BrainGraph').then(m => ({ default: m.BrainGraph }))
);
const BrainGraph3D = lazy(() =>
  import('./BrainGraph3D').then(m => ({ default: m.BrainGraph3D }))
);

// Strip a leading YAML frontmatter block and pull a couple of fields for a chip.
function splitFrontmatter(md: string): {
  body: string;
  status?: string;
  updated?: string;
} {
  const m = md.match(/^---\n([\s\S]*?)\n---\n?/);
  if (!m) return { body: md };
  const fm = m[1];
  const status = fm.match(/^status:\s*(.+)$/m)?.[1]?.trim();
  const updated = fm.match(/^updated:\s*(.+)$/m)?.[1]?.trim();
  return { body: md.slice(m[0].length), status, updated };
}

// Convert Obsidian [[wikilinks]] into markdown links with a #wiki: scheme the
// <a> renderer intercepts. Supports [[Target]] and [[Target|Alias]].
function wikilinksToLinks(md: string): string {
  return md.replace(
    /\[\[([^\]|]+)(?:\|([^\]]+))?\]\]/g,
    (_all, target: string, alias?: string) =>
      `[${(alias || target).trim()}](#wiki:${encodeURIComponent(target.trim())})`
  );
}

export function SystemMapScreen() {
  const { data: pages } = useBrainTree();
  const [graphDim, setGraphDim] = useState<'2d' | '3d'>('3d');
  const [graphExpanded, setGraphExpanded] = useState(false);
  // Path shown in the on-graph detail drawer (null = closed).
  const [graphPanelPath, setGraphPanelPath] = useState<string | null>(null);

  // Esc closes the detail drawer first, then exits fullscreen.
  useEffect(() => {
    if (!graphExpanded && !graphPanelPath) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (graphPanelPath) setGraphPanelPath(null);
      else setGraphExpanded(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [graphExpanded, graphPanelPath]);

  // Resolve a wikilink target (a page title) → its path, for drawer navigation.
  const titleToPath = useMemo(() => {
    const m = new Map<string, string>();
    (pages || []).forEach(p => m.set(p.title.toLowerCase(), p.path));
    return m;
  }, [pages]);

  return (
    <div className="space-y-5">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold text-nurev-text lg:text-2xl">
            <Network className="h-5 w-5 text-amber-400" /> System Map
          </h1>
          <p className="mt-1 text-xs text-nurev-muted">
            The NUREV Brain — an internal, admin-only map of the whole system
            (architecture, money path, compliance, build, product). Served from
            the <code className="code-pill">nurev-brain</code> vault. Click a
            node to open that page.
          </p>
        </div>
      </header>

      <div
        className={
          graphExpanded
            ? 'fixed inset-0 z-[60] flex flex-col gap-2 bg-nurev-bg p-4'
            : 'flex flex-col gap-3'
        }
      >
        <div className="flex justify-end gap-1">
          <div className="flex gap-1 rounded-lg bg-white/[0.03] p-1">
            {(['3d', '2d'] as const).map(d => (
              <button
                key={d}
                onClick={() => setGraphDim(d)}
                className={`rounded-md px-3 py-1 text-[11px] font-medium uppercase transition ${
                  graphDim === d
                    ? 'bg-amber-500 text-nurev-bg'
                    : 'text-nurev-muted hover:bg-white/[0.04] hover:text-nurev-text'
                }`}
              >
                {d}
              </button>
            ))}
          </div>
          <button
            onClick={() => setGraphExpanded(e => !e)}
            title={graphExpanded ? 'Collapse (Esc)' : 'Expand to full screen'}
            className="flex items-center gap-1.5 rounded-lg bg-white/[0.03] px-3 py-1 text-[11px] font-medium text-nurev-muted transition hover:bg-white/[0.04] hover:text-nurev-text"
          >
            {graphExpanded ? (
              <>
                <Minimize2 className="h-3.5 w-3.5" /> Collapse
              </>
            ) : (
              <>
                <Maximize2 className="h-3.5 w-3.5" /> Expand
              </>
            )}
          </button>
        </div>
        <div
          className={`relative ${
            graphExpanded ? 'min-h-0 flex-1' : 'h-[680px]'
          }`}
        >
          <Suspense fallback={<Skeleton className="h-full w-full rounded-xl" />}>
            {graphDim === '3d' ? (
              <BrainGraph3D onOpen={setGraphPanelPath} focusPath={graphPanelPath} />
            ) : (
              <BrainGraph onOpen={setGraphPanelPath} focusPath={graphPanelPath} />
            )}
          </Suspense>

          {graphPanelPath && (
            <aside className="absolute right-0 top-0 z-20 flex h-full w-[30%] min-w-[300px] flex-col rounded-r-xl border-l border-nurev-border bg-nurev-surface/80 backdrop-blur-md">
              <div className="flex items-center justify-between gap-2 border-b border-nurev-border px-3 py-2">
                <span className="truncate font-mono text-[10px] text-nurev-muted">
                  {graphPanelPath}
                </span>
                <button
                  onClick={() => setGraphPanelPath(null)}
                  title="Close (Esc)"
                  className="shrink-0 rounded p-1 text-nurev-muted transition hover:bg-white/[0.06] hover:text-nurev-text"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
                <PageView
                  path={graphPanelPath}
                  onSelect={setGraphPanelPath}
                  titleToPath={titleToPath}
                  bare
                />
              </div>
            </aside>
          )}
        </div>
      </div>
    </div>
  );
}

function PageView({
  path,
  onSelect,
  titleToPath,
  bare = false
}: {
  path: string | null;
  onSelect: (p: string) => void;
  titleToPath: Map<string, string>;
  bare?: boolean;
}) {
  const { data, isLoading, isError } = useBrainPage(path);

  const { body, status, updated } = useMemo(
    () => (data ? splitFrontmatter(data.markdown) : { body: '' }),
    [data]
  );
  const processed = useMemo(() => wikilinksToLinks(body), [body]);

  const components = useMemo<Components>(
    () => ({
      h1: ({ children }) => (
        <h1 className="mb-4 mt-1 text-2xl font-bold text-nurev-text">{children}</h1>
      ),
      h2: ({ children }) => (
        <h2 className="mb-3 mt-7 border-b border-nurev-border pb-1 text-lg font-bold text-nurev-text">
          {children}
        </h2>
      ),
      h3: ({ children }) => (
        <h3 className="mb-2 mt-5 text-sm font-semibold uppercase tracking-wide text-amber-300">
          {children}
        </h3>
      ),
      h4: ({ children }) => (
        <h4 className="mb-1.5 mt-4 text-sm font-semibold text-nurev-text">
          {children}
        </h4>
      ),
      p: ({ children }) => (
        <p className="mb-3 text-sm leading-relaxed text-nurev-text-soft">{children}</p>
      ),
      ul: ({ children }) => (
        <ul className="mb-3 list-disc space-y-1 pl-5 text-sm text-nurev-text-soft">
          {children}
        </ul>
      ),
      ol: ({ children }) => (
        <ol className="mb-3 list-decimal space-y-1 pl-5 text-sm text-nurev-text-soft">
          {children}
        </ol>
      ),
      li: ({ children }) => <li className="leading-relaxed">{children}</li>,
      strong: ({ children }) => (
        <strong className="font-semibold text-nurev-text">{children}</strong>
      ),
      em: ({ children }) => <em className="italic">{children}</em>,
      hr: () => <hr className="my-5 border-nurev-border" />,
      blockquote: ({ children }) => (
        <blockquote className="mb-3 border-l-2 border-amber-500/40 pl-3 text-sm italic text-nurev-muted">
          {children}
        </blockquote>
      ),
      a: ({ href, children }) => {
        if (href && href.startsWith('#wiki:')) {
          const target = decodeURIComponent(href.slice('#wiki:'.length));
          const resolved = titleToPath.get(target.toLowerCase());
          if (resolved) {
            return (
              <button
                onClick={() => onSelect(resolved)}
                className="text-amber-300 underline decoration-dotted underline-offset-2 hover:text-amber-200"
              >
                {children}
              </button>
            );
          }
          return (
            <span
              className="text-nurev-muted underline decoration-dotted decoration-nurev-muted/50"
              title={`Unresolved link: ${target}`}
            >
              {children}
            </span>
          );
        }
        return (
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className="text-amber-300 underline hover:text-amber-200"
          >
            {children}
          </a>
        );
      },
      code: ({ className, children }) => {
        const txt = String(children ?? '');
        const isBlock = (className?.includes('language-') ?? false) || txt.includes('\n');
        if (isBlock) {
          return (
            <code className="font-mono text-xs leading-relaxed text-nurev-text-soft">
              {children}
            </code>
          );
        }
        return (
          <code className="rounded bg-nurev-surface-lift px-1.5 py-0.5 font-mono text-[0.8em] text-amber-200">
            {children}
          </code>
        );
      },
      pre: ({ children }) => (
        <pre className="mb-3 overflow-x-auto rounded-lg border border-nurev-border bg-nurev-surface p-3">
          {children}
        </pre>
      ),
      table: ({ children }) => (
        <div className="mb-4 overflow-x-auto">
          <table className="w-full border-collapse text-sm">{children}</table>
        </div>
      ),
      th: ({ children }) => (
        <th className="border-b border-nurev-border px-3 py-2 text-left text-[10px] font-medium uppercase tracking-wider text-nurev-muted">
          {children}
        </th>
      ),
      td: ({ children }) => (
        <td className="border-b border-nurev-border/50 px-3 py-2 align-top text-nurev-text-soft">
          {children}
        </td>
      )
    }),
    [onSelect, titleToPath]
  );

  if (!path) {
    return (
      <div className="rounded-xl border border-nurev-border bg-nurev-surface p-8 text-center text-sm text-nurev-muted">
        Select a page.
      </div>
    );
  }
  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-7 w-1/2" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-5 text-sm text-red-300">
        Couldn't load <code className="code-pill">{path}</code>.
      </div>
    );
  }

  const meta = (
    <div className="mb-4 flex flex-wrap items-center gap-2 text-[10px] text-nurev-muted">
      <span className="font-mono">{data.path}</span>
      {status && (
        <span className="rounded bg-white/10 px-1.5 py-0.5 uppercase tracking-wide ring-1 ring-white/10">
          {status}
        </span>
      )}
      {updated && <span>updated {updated}</span>}
    </div>
  );
  const md = (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {processed}
    </ReactMarkdown>
  );

  // Bare = rendered inside a container that already provides chrome (the
  // on-graph detail drawer); full = the standalone Reader card.
  if (bare) {
    return (
      <div>
        {meta}
        {md}
      </div>
    );
  }
  return (
    <article className="rounded-xl border border-nurev-border bg-nurev-surface px-6 py-5">
      {meta}
      {md}
    </article>
  );
}
